import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, CheckCircle, AlertCircle } from 'lucide-react';
import apiClient, { endpoints } from '../api';
import './LocationSetup.css';

const LocationSetup = ({ onLocationSet, onSkip, initialAddress, vendorId }) => {
  const [address, setAddress] = useState(initialAddress || '');
  const [coordinates, setCoordinates] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);

  useEffect(() => {
    // Load Google Maps API
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setIsMapLoaded(true);
        initializeMap();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY'}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsMapLoaded(true);
        initializeMap();
      };
      script.onerror = () => {
        setMapFailed(true);
        setError('Failed to load Google Maps. Using demo mode.');
      };
      document.head.appendChild(script);
    };

    loadGoogleMaps();

    // Fallback timeout in case script neither loads nor errors
    const timeoutId = setTimeout(() => {
      if (!window.google || !window.google.maps) {
        setMapFailed(true);
      }
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, []);

  // Auto-geocode the initial address once map (or fallback) is ready
  useEffect(() => {
    if (address && !coordinates && (isMapLoaded || mapFailed)) {
      handleGeocode();
    }
  }, [address, coordinates, isMapLoaded, mapFailed]);

  const initializeMap = () => {
    if (!window.google || !window.google.maps) return;

    const mapElement = document.getElementById('map');
    if (!mapElement) return;

    const defaultCenter = { lat: 28.6139, lng: 77.2090 }; // Delhi coordinates
    const mapInstance = new window.google.maps.Map(mapElement, {
      zoom: 15,
      center: defaultCenter,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true
    });

    setMap(mapInstance);

    // Add click listener to map
    mapInstance.addListener('click', (event) => {
      const clickedCoordinates = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      setCoordinates(clickedCoordinates);
      updateMarker(clickedCoordinates, mapInstance);
      
      // Reverse geocode the clicked location
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: clickedCoordinates }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setAddress(results[0].formatted_address);
        }
      });
    });
  };

  const updateMarker = (coords, mapInstance) => {
    if (marker) {
      marker.setMap(null);
    }

    const newMarker = new window.google.maps.Marker({
      position: coords,
      map: mapInstance,
      title: 'Shop Location',
      draggable: true
    });

    newMarker.addListener('dragend', (event) => {
      const newCoords = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      setCoordinates(newCoords);
      
      // Reverse geocode the new position
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newCoords }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setAddress(results[0].formatted_address);
        }
      });
    });

    setMarker(newMarker);
    mapInstance.panTo(coords);
  };

  const handleAddressChange = (e) => {
    setAddress(e.target.value);
    setError('');
  };

  const handleGeocode = async () => {
    if (!address.trim()) {
      setError('Please enter an address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (window.google && window.google.maps) {
        // Use Google Maps Geocoding API
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: address }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const location = results[0].geometry.location;
            const coordinates = {
              lat: location.lat(),
              lng: location.lng()
            };
            setCoordinates(coordinates);
            setAddress(results[0].formatted_address);
            if (map) {
              updateMarker(coordinates, map);
            }
          } else {
            setError('Address not found. Please try a different address.');
          }
          setIsLoading(false);
        });
      } else {
        // Fallback to mock coordinates if Google Maps is not available
        await new Promise(resolve => setTimeout(resolve, 1000));
        const mockCoordinates = {
          lat: 28.6139 + (Math.random() - 0.5) * 0.1,
          lng: 77.2090 + (Math.random() - 0.5) * 0.1
        };
        setCoordinates(mockCoordinates);
        if (map) {
          updateMarker(mockCoordinates, map);
        }
        setIsLoading(false);
      }
    } catch (err) {
      setError('Failed to geocode address. Please try again.');
      setIsLoading(false);
    }
  };

  const handleAutoGeocode = async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const { latitude, longitude } = position.coords;
      const coordinates = { lat: latitude, lng: longitude };
      setCoordinates(coordinates);
      if (map) {
        updateMarker(coordinates, map);
      }
      
      // Reverse geocode to get address
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: coordinates }, (results, status) => {
          if (status === 'OK' && results[0]) {
            setAddress(results[0].formatted_address);
          } else {
            setAddress(`Auto-detected location (${latitude.toFixed(6)}, ${longitude.toFixed(6)})`);
          }
          setIsLoading(false);
        });
      } else {
        setAddress(`Auto-detected location (${latitude.toFixed(6)}, ${longitude.toFixed(6)})`);
        setIsLoading(false);
      }
    } catch (err) {
      setError('Failed to get current location. Please enter address manually.');
      setIsLoading(false);
    }
  };

  const handleConfirmLocation = async () => {
    if (!coordinates) {
      setError('Please set a location first');
      return;
    }

    if (vendorId) {
      try {
        setIsLoading(true);
        const response = await apiClient.put(endpoints.updateLocation(vendorId), {
          address,
          coordinates
        });

        if (response.status === 200) {
          onLocationSet({
            address,
            coordinates,
            timestamp: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('Location update error:', error);
        setError('Failed to save location. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      onLocationSet({
        address,
        coordinates,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  return (
    <div className="location-setup-container">
      <div className="location-setup-card">
        <div className="location-setup-header">
          <div className="location-setup-logo">
            <MapPin size={24} />
            <h2>Location Setup</h2>
          </div>
          <p className="location-setup-subtitle">
            Pin your shop location on the map for better service delivery
          </p>
        </div>

        <div className="location-setup-content">
          <div className="location-input-section">
            <label htmlFor="address" className="form-label">
              Shop Address
            </label>
            <div className="address-input-container">
              <input
                type="text"
                id="address"
                value={address}
                onChange={handleAddressChange}
                className="address-input"
                placeholder="Enter your shop address"
                disabled={isLoading}
              />
              <button
                type="button"
                className="geocode-btn"
                onClick={handleGeocode}
                disabled={isLoading || !address.trim()}
              >
                {isLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
            
            <div className="auto-location-section">
              <button
                type="button"
                className="auto-location-btn"
                onClick={handleAutoGeocode}
                disabled={isLoading}
              >
                <Navigation size={16} />
                Use Current Location
              </button>
            </div>

            {error && (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="map-section">
            <div className="map-container">
              {isMapLoaded && !mapFailed && (
                <div id="map" style={{ width: '100%', height: '400px', borderRadius: '8px' }}></div>
              )}
              {!isMapLoaded && !mapFailed && (
                <div className="map-loading">
                  <div className="spinner"></div>
                  <p>Loading map...</p>
                </div>
              )}
              {mapFailed && (
                <div className="map-placeholder">
                  <MapPin size={32} />
                  <p>Map unavailable. You can still set location using Search or Current Location.</p>
                  <small>Demo mode: coordinates will be approximated.</small>
                </div>
              )}
            </div>
            <div className="map-instructions">
              <p><strong>Instructions:</strong></p>
              <ul>
                <li>Enter your address above and click "Search" to find your location</li>
                <li>Or click "Use Current Location" to auto-detect your position</li>
                <li>Click anywhere on the map to set your shop location</li>
                <li>Drag the marker to fine-tune your location</li>
              </ul>
            </div>
          </div>

          {coordinates && (
            <div className="location-confirmation">
              <div className="location-info">
                <CheckCircle size={20} className="success-icon" />
                <div>
                  <p><strong>Location Set:</strong> {address}</p>
                  <p><small>Coordinates: {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}</small></p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="location-setup-actions">
          <button
            type="button"
            className="skip-btn"
            onClick={handleSkip}
            disabled={isLoading}
          >
            Skip for Now
          </button>
          <button
            type="button"
            className="confirm-btn"
            onClick={handleConfirmLocation}
            disabled={!coordinates || isLoading}
          >
            Confirm Location
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationSetup;
